import throttle from 'lodash/throttle';

import { writeHtmlToOsClipboard } from '@canvas/clipboard';
import { type PasteOrDropPayload } from '@canvas/clipboard/types';
import { type CanvasHTMLElement } from '@canvas/input-types/base-event-types';
import { type CanvasEventTarget } from '@canvas/input-types/event-targets';
import { type PointerButton } from '@canvas/input-types/pointer-events-types';

import { getInputTarget } from '../input-target/get-input-target.ts';
import {
  type Capture,
  type CaptureListener,
  type CaptureType,
  type IdCallback,
} from '../input-types.ts';

// If the document's active element matches this selector, we should allow native copy
const allowCopySelectorActive = 'input, text, [contenteditable="true"]';

// If the copy event's target or any of its parents match this selector, we should
// allow native copy
const allowCopySelectorClosest = '[data-is-text-selectable]';

// Returns true if the document's active element is itself editable
function isActiveElementEditable() {
  const activeElement =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  return activeElement?.matches(allowCopySelectorActive) ?? false;
}

// Returns true if the copy event should be allowed to proceed natively
export function shouldAllowCopy(target: EventTarget | null) {
  const targetElement = target instanceof HTMLElement ? target : null;
  const allowCopyClosestElement =
    targetElement?.closest(allowCopySelectorClosest) != null;

  return isActiveElementEditable() || allowCopyClosestElement;
}

/**
 * Prefers the focused element over the paste event's own target: browsers can
 * dispatch `paste` at whatever DOM node the pointer last hovered (e.g. a sidebar
 * panel) even while nothing there is focused, which would otherwise misattribute
 * the paste and have it swallowed instead of landing on the board. Still defers to
 * the event's target inside an open native `<dialog>`, since a real modal blocks
 * board interaction regardless of focus, unlike a coexisting sidebar panel.
 */
function resolvePasteTarget(
  evt: Event,
  activeElementAtPasteTime: Element | null,
): CanvasEventTarget {
  const activeTarget = getInputTarget(activeElementAtPasteTime);
  if (activeTarget !== 'board' && activeTarget !== 'none') {
    return activeTarget;
  }

  const eventTarget = evt.target instanceof Element ? evt.target : null;
  return eventTarget?.closest('dialog[open]') ? getInputTarget(evt.target) : activeTarget;
}

export const setupClipboardEvents = (
  root: CanvasHTMLElement,
  id: IdCallback,
  onCapture: (capture: Capture) => void,
  captureContext: {
    x?: number;
    y?: number;
    button?: PointerButton;
  },
  listeners: Map<CaptureType, CaptureListener>,
) => {
  const onCut = (evt: ClipboardEvent) => {
    if (shouldAllowCopy(evt.target)) {
      return false;
    }

    if (evt.clipboardData == null) {
      return;
    }

    const copied = onCopy(evt);

    if (!copied) {
      return;
    }

    onCapture({
      id: id(),
      type: 'delete',
      target: getInputTarget(evt.target),
      properties: {},
    });
  };

  const onCopy = (evt: ClipboardEvent): boolean => {
    // We should defer to the native clipboard when input or textareas are active
    if (shouldAllowCopy(evt.target)) {
      return false;
    }

    evt.preventDefault();

    const copyPayload = listeners.get('copy')?.();
    if (!copyPayload) {
      return false;
    }

    if (evt.clipboardData) {
      // For real clipboard events from the browser, use clipboardData
      evt.clipboardData.setData('text/html', copyPayload);
    } else {
      // For synthetic events (e.g., from database iframe forwarding),
      // use the Clipboard API to write directly to the OS clipboard
      // It is technically impossible to use .clipboardData.setData as
      // synthetic events cannot set the clipboard data to that of the OS
      writeHtmlToOsClipboard(copyPayload);
    }

    return true;
  };

  /**
   * We throttle paste events since some OS's will let the user
   * hold down the shortcut, causing a rapid burst of paste events that
   * can be dangerous to our system.
   */
  const onPasteCapture = throttle(
    (
      evt: Event,
      pastePayload: PasteOrDropPayload,
      activeElementAtPasteTime: Element | null,
      position: { x?: number; y?: number } = { x: captureContext.x, y: captureContext.y },
    ) => {
      {
        if (captureContext.button === 'middle') {
          // On some linux distros, the middle button triggers an unwanted paste.
          return;
        }
        onCapture({
          id: id(),
          type: 'paste',
          target: resolvePasteTarget(evt, activeElementAtPasteTime),
          properties: {
            clipboardContent: pastePayload,
            x: position.x ?? globalThis.innerWidth / 2,
            y: position.y ?? globalThis.innerHeight / 2,
          },
        });
      }
    },
    200,
    { leading: true, trailing: false },
  );

  const onPaste = (evt: ClipboardEvent) => {
    // Unlike copy, there's no unfocused text to paste into, so only a focused
    // editable element should defer to native paste.
    if (isActiveElementEditable()) {
      return;
    }

    if (evt.clipboardData == null) {
      return;
    }

    // Read before the async paste listener below, so it reflects focus at paste
    // time rather than whenever that promise happens to resolve.
    const activeElementAtPasteTime = document.activeElement;

    listeners
      .get('paste')?.(evt)
      .then((pastePayload: PasteOrDropPayload | undefined) => {
        if (pastePayload) {
          onPasteCapture(evt, pastePayload, activeElementAtPasteTime);
        }
      });
  };

  root.addEventListener('copy', onCopy);
  root.addEventListener('cut', onCut);
  root.addEventListener('paste', onPaste);

  return () => {
    root.removeEventListener('copy', onCopy);
    root.removeEventListener('cut', onCut);
    root.removeEventListener('paste', onPaste);
  };
};
